import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminUserAPI } from '@/services/api';
import type { AdminUser } from '@/types/domain';

export interface UseAdminUsersReturn {
  users: AdminUser[];
  loading: boolean;
  error: string | null;
  success: string | null;
  refresh: () => Promise<void>;
  create: (data: { username: string; password: string; email?: string }) => Promise<void>;
  update: (id: number, data: { password?: string; email?: string }) => Promise<void>;
  delete: (id: number) => Promise<void>;
  clearMessages: () => void;
}

/**
 * 管理员用户数据管理 Hook
 * 封装用户的增删改查操作和状态管理
 */
export function useAdminUsers(): UseAdminUsersReturn {
  const { t } = useTranslation();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 组件挂载时加载用户列表
  useEffect(() => {
    void loadUsers();
  }, []);

  /**
   * 加载用户列表
   */
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminUserAPI.getAll();
      setUsers(response.data || []);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      if (err.response?.status === 401) {
        setError(t('users.sessionExpired'));
      } else {
        setError(t('users.loadFailed'));
      }
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * 创建新用户
   */
  const create = useCallback(
    async (data: { username: string; password: string; email?: string }) => {
      // 验证必填字段
      if (!data.username || !data.password) {
        throw new Error(t('users.username') + ' & ' + t('users.password') + ' ' + t('common.required') || 'Username and password are required');
      }

      // 验证密码长度
      if (data.password.length < 6) {
        throw new Error(t('users.passwordTooShort'));
      }

      try {
        const createData: { username: string; password: string; email?: string } = {
          username: data.username,
          password: data.password,
        };
        if (data.email) {
          createData.email = data.email;
        }
        await adminUserAPI.create(createData);
        setSuccess(t('users.createdSuccess'));
        await loadUsers();
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || t('users.createFailed');
        setError(errorMessage);
        throw err;
      }
    },
    [t, loadUsers]
  );

  /**
   * 更新用户
   */
  const update = useCallback(
    async (id: number, data: { password?: string; email?: string }) => {
      try {
        const updateData: { password?: string; email?: string } = {};
        if (data.password) {
          updateData.password = data.password;
        }
        if (data.email) {
          updateData.email = data.email;
        }
        await adminUserAPI.update(id, updateData);
        setSuccess(t('users.updatedSuccess'));
        await loadUsers();
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || t('users.updateFailed');
        setError(errorMessage);
        throw err;
      }
    },
    [t, loadUsers]
  );

  /**
   * 删除用户
   */
  const deleteUser = useCallback(
    async (id: number) => {
      if (!confirm(t('users.confirmDeactivate'))) {
        return;
      }

      try {
        await adminUserAPI.delete(id);
        setSuccess(t('users.deletedSuccess'));
        await loadUsers();
      } catch (err: any) {
        setError(err.response?.data?.error || t('users.deleteFailed'));
        throw err;
      }
    },
    [t, loadUsers]
  );

  /**
   * 清除错误和成功消息
   */
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  return {
    users,
    loading,
    error,
    success,
    refresh: loadUsers,
    create,
    update,
    delete: deleteUser,
    clearMessages,
  };
}
